import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { X, Plus } from "lucide-react";
import { cardCls, inputCls, labelCls, fmtDate } from "./ui";

const INTERESTS = ["GPU & AI Compute", "Computer Components", "Memory & Storage", "Servers & Workstations", "Networking", "Consumer Technology", "Mobile & Accessories", "Gaming / Consoles", "All Offers"];
const BUSINESS_TYPES = ["Distributor", "Reseller", "System Integrator", "Data Centre", "Cloud Operator", "Enterprise", "Government / Institution", "Retailer", "Other"];

const EMPTY = { name: "", subject: "", body: "", interests: [], countries: [], business_types: [], status: "draft", scheduled_at: "" };

const Editor = ({ campaign, onClose, onSave }) => {
  const [form, setForm] = useState(campaign);
  const [estimate, setEstimate] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggle = (k, v) => setForm((f) => ({ ...f, [k]: f[k].includes(v) ? f[k].filter((x) => x !== v) : [...f[k], v] }));

  const doEstimate = async () => {
    const { data } = await api.post("/admin/campaigns/estimate", { interests: form.interests, countries: form.countries, business_types: form.business_types });
    setEstimate(data.recipients);
  };

  return (
    <div className="fixed inset-0 z-50 bg-midnight/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="campaign-editor-modal">
      <div className="bg-white hairline shadow-medium max-w-2xl w-full max-h-[85vh] overflow-y-auto p-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-6">
          <h3 className="font-heading text-lg text-midnight">{form.id ? "Edit Campaign" : "New Campaign"}</h3>
          <button onClick={onClose} className="text-midnight/40 hover:text-midnight" aria-label="Close"><X size={18} /></button>
        </div>
        <div className="space-y-4">
          <div><label className={labelCls}>Campaign Name *</label><input value={form.name} onChange={set("name")} data-testid="campaign-name" className={inputCls} /></div>
          <div><label className={labelCls}>Subject</label><input value={form.subject} onChange={set("subject")} data-testid="campaign-subject" className={inputCls} /></div>
          <div><label className={labelCls}>Body</label><textarea rows={4} value={form.body} onChange={set("body")} data-testid="campaign-body" className={inputCls} /></div>
          <div>
            <label className={labelCls}>Audience — Product Interests</label>
            <div className="flex flex-wrap gap-2">
              {INTERESTS.map((i) => (
                <button type="button" key={i} onClick={() => toggle("interests", i)} className={`text-[11px] font-medium px-3 py-1.5 transition-colors ${form.interests.includes(i) ? "bg-midnight text-white" : "bg-white text-midnight/60 hairline"}`}>{i}</button>
              ))}
            </div>
          </div>
          <div>
            <label className={labelCls}>Audience — Business Types</label>
            <div className="flex flex-wrap gap-2">
              {BUSINESS_TYPES.map((t) => (
                <button type="button" key={t} onClick={() => toggle("business_types", t)} className={`text-[11px] font-medium px-3 py-1.5 transition-colors ${form.business_types.includes(t) ? "bg-midnight text-white" : "bg-white text-midnight/60 hairline"}`}>{t}</button>
              ))}
            </div>
          </div>
          <div>
            <label className={labelCls}>Audience — Countries (comma separated)</label>
            <input value={form.countries.join(", ")} onChange={(e) => setForm((f) => ({ ...f, countries: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) }))} data-testid="campaign-countries" className={inputCls} placeholder="e.g. United Arab Emirates, Saudi Arabia" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Status</label>
              <select value={form.status} onChange={set("status")} data-testid="campaign-status" className={inputCls}>
                <option value="draft">Draft</option>
                <option value="scheduled">Scheduled</option>
                <option value="sent">Sent</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Scheduled At</label>
              <input type="datetime-local" value={form.scheduled_at} onChange={set("scheduled_at")} className={inputCls} />
            </div>
          </div>
          <div className="flex items-center gap-4 pt-2">
            <button onClick={doEstimate} data-testid="campaign-estimate" className="text-xs font-bold tracking-wide text-midnight border border-midnight/20 px-5 py-2.5 hover:border-gold hover:text-gold transition-colors">
              ESTIMATE AUDIENCE
            </button>
            {estimate !== null && <span className="text-sm text-midnight"><span className="font-semibold text-gold">{estimate}</span> matching recipients</span>}
          </div>
        </div>
        <button onClick={() => onSave(form)} data-testid="campaign-save" className="mt-6 w-full bg-midnight text-white text-sm font-semibold py-3.5 hover:bg-midnight-light transition-colors">Save Campaign</button>
      </div>
    </div>
  );
};

export const CampaignsSection = () => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/campaigns").then((r) => setItems(r.data.items)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async (form) => {
    if (!form.name.trim()) { toast.error("Campaign name is required"); return; }
    if (form.id) await api.put(`/admin/campaigns/${form.id}`, form);
    else await api.post("/admin/campaigns", form);
    setEditing(null);
    load();
    toast.success("Campaign saved");
  };

  const remove = async (id) => {
    await api.delete(`/admin/campaigns/${id}`);
    load();
    toast.success("Campaign deleted");
  };

  return (
    <div data-testid="admin-campaigns-section">
      <button onClick={() => setEditing({ ...EMPTY })} data-testid="campaign-add" className="mb-5 flex items-center gap-2 bg-midnight text-white text-sm font-medium px-5 py-3 hover:bg-midnight-light transition-colors">
        <Plus size={14} /> New Campaign
      </button>
      <div className={cardCls}>
        {items.length === 0 && <p className="px-5 py-10 text-sm text-midnight/40">No campaigns yet. Sending infrastructure connects when your email service is configured.</p>}
        {items.map((c) => (
          <div key={c.id} className="grid grid-cols-2 md:grid-cols-12 gap-3 items-center px-5 py-4 border-b border-midnight/[0.06]" data-testid={`campaign-${c.id}`}>
            <span className="text-sm font-medium text-midnight md:col-span-3">{c.name}</span>
            <span className="text-xs text-midnight/55 md:col-span-4">{c.subject || "—"}</span>
            <span className="text-xs text-midnight/50 md:col-span-1">{c.stats?.recipients || 0} rcpt</span>
            <span className="text-xs text-midnight/50 hidden md:block">{fmtDate(c.created_at)}</span>
            <span className={`text-[11px] font-bold uppercase ${c.status === "sent" ? "text-emerald-600" : c.status === "scheduled" ? "text-gold" : "text-midnight/45"}`}>{c.status}</span>
            <span className="flex gap-4 justify-end md:col-span-2">
              <button onClick={() => setEditing(c)} className="text-[11px] font-bold tracking-wide text-midnight hover:text-gold">EDIT</button>
              <button onClick={() => remove(c.id)} className="text-[11px] font-bold tracking-wide text-midnight/30 hover:text-red-500">DELETE</button>
            </span>
          </div>
        ))}
      </div>
      {editing && <Editor campaign={editing} onClose={() => setEditing(null)} onSave={save} />}
    </div>
  );
};
